/**
 * UI slice — Phase D foundation (May 6, 2026).
 *
 * Extracted from `store/useStore.js` as a pure structural split.
 *
 * Scope:
 *   - Modal visibility flags + their setters
 *   - Viewport zoom + snap-to-grid preferences
 *   - Responsive warning flag (driven by App.js width check)
 *
 * Contract preservation rules (zero regression):
 *   1. Field & action names are byte-identical to the previous store.
 *   2. Persist shape unchanged — only `zoom` and `snapEnabled` are
 *      partialized at the root store level (see useStore.js partialize).
 *      The slice does NOT add or remove anything from `partialize`.
 *   3. Setter side-effects (clamping, toggling) match exactly.
 */

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;

export const createUiSlice = (set) => ({
  // ----- state -----
  isLoading: false,

  zoom: 1,
  snapEnabled: true,
  snapThreshold: 6, // pixels

  showCartLinkModal: false,
  showImageUploadModal: false,
  showEditRoomModal: false,
  showResponsiveWarning: false,
  showStyleSettingsModal: false,
  showLoadProjectModal: false,
  showDiscardChangesDialog: false,

  // ----- actions -----
  // Zoom
  setZoom: (zoom) => set({ zoom: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom)) }),
  zoomIn: () => set((state) => ({ zoom: Math.min(ZOOM_MAX, state.zoom + ZOOM_STEP) })),
  zoomOut: () => set((state) => ({ zoom: Math.max(ZOOM_MIN, state.zoom - ZOOM_STEP) })),

  // Snap
  toggleSnap: () => set((state) => ({ snapEnabled: !state.snapEnabled })),
  setSnapEnabled: (enabled) => set({ snapEnabled: enabled }),

  // Modals
  setShowCartLinkModal: (show) => set({ showCartLinkModal: show }),
  setShowImageUploadModal: (show) => set({ showImageUploadModal: show }),
  setShowEditRoomModal: (show) => set({ showEditRoomModal: show }),
  setShowResponsiveWarning: (show) => set({ showResponsiveWarning: show }),
  setShowStyleSettingsModal: (show) => set({ showStyleSettingsModal: show }),
  setShowLoadProjectModal: (show) => set({ showLoadProjectModal: show }),
  setShowDiscardChangesDialog: (show) => set({ showDiscardChangesDialog: show }),
});
